import { useEffect, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import useSWR, { useSWRConfig } from 'swr';
import GuestUser from './guest-user';
import { LOYALTY_DEFAULTS } from './constants';
import {
  fetchLoyaltyDetailsSWR,
  fetchStoreLoyaltySettingsSWR,
  fetchPreSignupDataSWR,
  getLoyaltyDetailsUrl,
  getStoreLoyaltySettingsUrl,
  getPreSignupDataUrl,
  fetchLoyaltyDetailsSuccess,
  fetchLoyaltyDetailsFailure,
  fetchStoreLoyaltySettingsSuccess,
  fetchStoreLoyaltySettingsFailure,
  fetchPreSignupDataSuccess,
  fetchPreSignupDataFailure,
} from './actions';
import { setState } from 'containers/Home/actions';
import { toggleMobileCart } from 'containers/Home/actions';
import { defaultHomePropsState } from 'constants/common';
import LoyaltyDashboard from './dashboard';
import { CloseButtonCWUI } from '@next-order/next-order-components';
import AnimationWrapper from 'components/AnimationWrapper';
import './index.css';
import './media-queries.css';
import LottieAnimation from '../Lottie';
import EnrollmentForm from './EnrollmentForm';
import { BREAKPOINT_VALUES } from 'helpers/enums';

export default function Loyaltyv2({
  userDetails,
  storeConfig,
  setHomeState,
  breakPoint,
}) {
  const dispatch = useDispatch();

  // Track which data has been fetched
  const dataFetchedRef = useRef({
    settings: false,
    loyalty: false,
    preSignupData: false,
  });

  // Add state to track initial loading
  const [initialLoadComplete, setInitialLoadComplete] = useState(false);

  // Get isFetchingUser from the reducer to know if user data is still loading
  const { isFetchingUser } = useSelector((state) => state.homeReducer);

  const {
    userLoyaltyDetails,
    isLoading,
    error,
    storeLoyaltySettings,
    isLoadingSettings,
    settingsError,
    earnings,
    promotions,
    redemptions,
    isLoadingPreSignupData,
    preSignupDataError,
    preSignupData,
    isShowEnrollmentForm,
  } = useSelector((state) => state.loyaltyReducer);

  // Create SWR URLs
  const loyaltyDetailsUrl =
    userDetails?._id && storeLoyaltySettings?.id
      ? getLoyaltyDetailsUrl(userDetails._id, storeLoyaltySettings.id)
      : null;
  // Only make API call if we don't already have loyalty settings
  const storeLoyaltySettingsUrl =
    storeConfig?.restaurantId && !storeLoyaltySettings
      ? getStoreLoyaltySettingsUrl(
          storeConfig.restaurantId,
          storeConfig.storeId
        )
      : null;

  const { mutate } = useSWRConfig();

  // Use SWR for loyalty details
  const {
    data: swrLoyaltyDetails,
    error: swrLoyaltyError,
    isLoading: isLoyaltyLoading,
    isValidating,
  } = useSWR(loyaltyDetailsUrl, fetchLoyaltyDetailsSWR, {
    revalidateOnFocus: false,
    dedupingInterval: 60000, // 1 minute
    shouldRetryOnError: false,
    revalidateOnMount: true,
    onSuccess: (data) => {
      if (data) {
        dispatch(fetchLoyaltyDetailsSuccess(data));
      } else {
        dispatch(fetchLoyaltyDetailsSuccess(null)); // User not enrolled
      }
    },
    onError: (err) => {
      dispatch(fetchLoyaltyDetailsSuccess(null));
      dispatch(fetchLoyaltyDetailsFailure(err.message));
    },
  });

  useEffect(() => {
    if (!loyaltyDetailsUrl) return;
    mutate(loyaltyDetailsUrl);
  }, [loyaltyDetailsUrl, mutate]);

  // Use SWR for store loyalty settings (only if not already loaded)
  const {
    data: swrStoreLoyaltySettings,
    error: swrSettingsError,
    isLoading: isLoadingStoreSettings,
  } = useSWR(storeLoyaltySettingsUrl, fetchStoreLoyaltySettingsSWR, {
    revalidateOnFocus: false,
    dedupingInterval: 120000, // 2 minutes
    shouldRetryOnError: false,
    onSuccess: (data) => {
      if (data) {
        dispatch(fetchStoreLoyaltySettingsSuccess(data));
      }
    },
    onError: (err) => {
      dispatch(fetchStoreLoyaltySettingsFailure(err.message));
    },
  });

  // Check if store has loyalty enabled
  const isLoyaltyEnabled = storeLoyaltySettings?.isActive === true;

  // Is user authentication state still being determined?
  const isUserLoading = isFetchingUser === true;

  // Make sure we don't create the presignup URL until proper conditions are met
  const accountApiCompleted = userDetails?._id
    ? userLoyaltyDetails !== undefined && !isLoyaltyLoading
    : true;

  // Set conditions for when to fetch presignup data
  const shouldFetchPresignup =
    !isUserLoading &&
    isLoyaltyEnabled &&
    accountApiCompleted &&
    ((!userDetails?._id && !isUserLoading) ||
      (userDetails?._id && userLoyaltyDetails === null));

  // Create key array for presignup API
  const preSignupDataKey =
    shouldFetchPresignup && storeLoyaltySettings?.id
      ? [
          `presignup-v4`,
          storeLoyaltySettings.id,
          accountApiCompleted,
          !isUserLoading,
        ]
      : null;

  // Use SWR for presignup data
  const { data: swrPreSignupData, error: swrPreSignupError } = useSWR(
    preSignupDataKey,
    async ([_, settingId]) => {
      const url = getPreSignupDataUrl(settingId);
      return fetchPreSignupDataSWR(url);
    },
    {
      revalidateOnFocus: false,
      dedupingInterval: 60000, // 1 minute
      shouldRetryOnError: false,
      onSuccess: (data) => {
        if (data) {
          dispatch(fetchPreSignupDataSuccess(data));
        }
      },
      onError: (err) => {
        dispatch(fetchPreSignupDataFailure(err.message));
      },
    }
  );

  // Get the first store settings or use defaults
  const storeSettings = storeLoyaltySettings ? storeLoyaltySettings : null;

  // Get terminology or use default
  const pointsTerminology = storeSettings?.terminology || 'points';

  // Get theme colors or use defaults
  const themeColors = storeSettings?.themeSettings
    ? {
        backgroundColor:
          storeSettings.themeSettings.selectedBackgroundColor || '#f9f9f9',
        labelTextColor:
          storeSettings.themeSettings.selectedLabelTextColor || '#333',
        mainTextColor:
          storeSettings.themeSettings.selectedMainTextColor || '#666',
      }
    : {
        backgroundColor: '#333',
        labelTextColor: '#333',
        mainTextColor: '#666',
      };

  // Effect to determine when loading is complete
  // useEffect(() => {
  //   // Calculate if initial load is complete
  //   const settingsComplete = storeLoyaltySettings !== undefined || !!swrSettingsError;

  //   // Handle different cases for logged-in users vs guest users
  //   let userDataComplete = false;

  //   // If we're still determining user auth state, mark as not complete
  //   if (isUserLoading) {
  //     userDataComplete = false;
  //   } else if (userDetails?._id) {
  //     // For logged-in users, we need loyalty details
  //     userDataComplete = (userLoyaltyDetails !== undefined) || !!swrLoyaltyError;

  //     // If user is not enrolled, we also need presignup data to complete
  //     if (userLoyaltyDetails === null && isLoyaltyEnabled) {
  //       userDataComplete = userDataComplete && (preSignupData !== undefined || !!swrPreSignupError);
  //     }
  //   } else {
  //     // For guest users, we need presignup data only if loyalty is enabled
  //     userDataComplete = !isLoyaltyEnabled || preSignupData !== undefined || !!swrPreSignupError;
  //   }

  //   // Set initial load complete when all required data is loaded or we hit any error
  //   if (!initialLoadComplete &&
  //       ((settingsComplete && userDataComplete && !isUserLoading) ||
  //       error || settingsError || preSignupDataError)) {
  //     setInitialLoadComplete(true);
  //   }
  // }, [
  //   userDetails,
  //   userLoyaltyDetails,
  //   storeLoyaltySettings,
  //   preSignupData,
  //   initialLoadComplete,
  //   error,
  //   settingsError,
  //   preSignupDataError,
  //   isLoyaltyEnabled,
  //   swrLoyaltyError,
  //   swrSettingsError,
  //   swrPreSignupError,
  //   isLoading,
  //   isUserLoading
  // ]);
  // console.log(isLoyaltyLoading, isValidating, 'check both', isFetchingUser);
  // Calculate if we are still loading
  const isStillLoading =
    isUserLoading ||
    // (!initialLoadComplete) ||
    (storeLoyaltySettings === undefined &&
      !swrSettingsError &&
      storeLoyaltySettingsUrl) ||
    (userDetails?._id &&
      (userLoyaltyDetails === undefined || isLoyaltyLoading)) ||
    (shouldFetchPresignup && preSignupData === undefined && !swrPreSignupError);

  // Additional safety check for loyalty status
  const isLoyaltyStatusUncertain =
    userDetails?._id && (userLoyaltyDetails === undefined || isLoyaltyLoading);

  // Determine the current loading state for user feedback
  let loadingMessage = 'Loading loyalty details...';
  if (isUserLoading) {
    loadingMessage = 'Loading user information...';
  } else if (isLoyaltyStatusUncertain) {
    loadingMessage = 'Checking your loyalty status...';
  } else if (
    storeLoyaltySettings === undefined &&
    !swrSettingsError &&
    storeLoyaltySettingsUrl
  ) {
    loadingMessage = 'Loading store settings...';
  } else if (
    shouldFetchPresignup &&
    preSignupData === undefined &&
    !swrPreSignupError
  ) {
    loadingMessage = 'Loading loyalty offers...';
  }

  // Handle close button click
  const handleCloseClick = () => {
    dispatch(toggleMobileCart(false));
    dispatch(setState({ ...defaultHomePropsState }));
  };
  // Show loading state
  // Don't show loading during revalidation if we already have userLoyaltyDetails
  const shouldShowLoading =
    isStillLoading ||
    isLoadingSettings ||
    (isValidating && userLoyaltyDetails === undefined) ||
    (isValidating && userDetails?._id && userLoyaltyDetails === null);

  if (shouldShowLoading) {
    return (
      <div className='loyalty-loading'>
        <AnimationWrapper animation='fade-in'>
          <LottieAnimation
            statusText={loadingMessage}
            size='normal'
            backgroundColor={themeColors?.backgroundColor}
          />
        </AnimationWrapper>
      </div>
    );
  }

  // Show "Loyalty not enabled" message if store doesn't have loyalty
  if (!storeLoyaltySettings || !isLoyaltyEnabled) {
    return (
      <AnimationWrapper animation='fade-in'>
        <CloseButtonCWUI
          extendedClassName={`ml-5 mt-4 ${
            breakPoint <= BREAKPOINT_VALUES.DESKTOP ? 'hidden' : ''
          }`}
          onClick={handleCloseClick}
        />
        <div className='loyalty-loading'>Loyalty coming soon.</div>
      </AnimationWrapper>
    );
  }

  // // Show error state
  // if (error || swrLoyaltyError || settingsError || swrSettingsError || preSignupDataError || swrPreSignupError) {
  //   return (
  //     <>
  //       <CloseButtonCWUI extendedClassName="ml-5 mt-4" onClick={handleCloseClick} />
  //       <div className="loyalty-error">
  //         Error loading loyalty details: {error || swrLoyaltyError?.message || settingsError || swrSettingsError?.message || preSignupDataError || swrPreSignupError?.message}
  //       </div>
  //     </>
  //   );
  // }

  // Is the user both logged in AND enrolled in loyalty? Show the dashboard
  if (userDetails?._id && userLoyaltyDetails && userLoyaltyDetails !== null) {
    return (
      <AnimationWrapper animation='fade-in'>
        <CloseButtonCWUI
          extendedClassName={`ml-5 mt-4 ${
            breakPoint <= BREAKPOINT_VALUES.DESKTOP ? 'hidden' : ''
          }`}
          onClick={handleCloseClick}
        />
        <LoyaltyDashboard
          userDetails={userDetails}
          userLoyaltyDetails={userLoyaltyDetails}
          storeConfig={storeConfig}
          themeColors={themeColors}
          pointsTerminology={pointsTerminology}
          setHomeState={setHomeState}
        />
      </AnimationWrapper>
    );
  }

  if (userDetails?._id && isShowEnrollmentForm && !isValidating) {
    return (
      <AnimationWrapper animation='fade-in'>
        <EnrollmentForm
          breakPoint={breakPoint}
          enrollmentFields={storeLoyaltySettings.enrollmentFields}
        />
      </AnimationWrapper>
    );
  }
  // Otherwise show guest user component (for guests or non-enrolled users)
  return (
    <AnimationWrapper animation='fade-in'>
      <GuestUser
        breakPoint={breakPoint}
        storeConfig={storeConfig}
        preSignupData={preSignupData}
        currency={storeConfig?.currency || LOYALTY_DEFAULTS.CURRENCY_SYMBOL}
        storeSettings={storeSettings}
        themeColors={themeColors}
        pointsTerminology={pointsTerminology}
        userLoyaltyDetails={userLoyaltyDetails}
        setHomeState={setHomeState}
        storeLoyaltySettings={storeLoyaltySettings}
      />
    </AnimationWrapper>
  );
}
